// Imported as the very first thing in the app to ensure that things like
// logging and error reporting are setup and initialised before any other
// modules are imported and start trying to log things or potentially
// throw errors.
import './priorityInitialisation'

import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { GlobalStyles } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { LicenseInfo } from '@mui/x-license'
import { SnackbarProvider, SnackbarProviderProps } from 'notistack'
import { QueryClientProvider } from '@tanstack/react-query'

import dayjs from 'dayjs'
import 'dayjs/locale/en-nz'
import 'dayjs/locale/en-au'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import weekday from 'dayjs/plugin/weekday'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import minMax from 'dayjs/plugin/minMax'
import isLeapYear from 'dayjs/plugin/isLeapYear'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { logger } from '$utils'

import theme from './theme'
import router from './router'
import AppErrorBoundary from './components/common/app/AppErrorBoundary'
import LoadedSnackbar from './components/common/app/LoadedSnackbar'
import { singletonQueryClient } from './utils/queryClient'
import { Action, SNACKBAR_AUTO_HIDE_MS } from './utils/snackbar'

dayjs.extend(weekday)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.extend(advancedFormat)
dayjs.extend(minMax)
dayjs.extend(isLeapYear)

LicenseInfo.setLicenseKey(
  'b20b0e85270479da8ba79eb70e050dfcTz0xMjQ1MDUsRT0xODAwNDg5NTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
)

const snackbarProviderProps: SnackbarProviderProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  maxSnack: 3,
  autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
  action: Action,
  preventDuplicate: true,
  hideIconVariant: true,
  Components: {
    loadedSnackbar: LoadedSnackbar,
  },
}

logger.info('React app starting')

// We defer rendering of the app until the DOM is loaded
// this avoids a race condition where this code ends up
// running before the DOM element we try and render the
// app into is available.
//
// TODO - this deferring won't be required once we get rid of the AngularJS
// app 'host'. The #react-container div is currently being rendered by the
// AngularJS app so it isn't immediately availahle.
window.addEventListener('DOMContentLoaded', () => {
  const container: HTMLElement | null =
    document.getElementById('react-container')

  if (container) {
    logger.info('Rendering Loaded React app')

    router.subscribe((state) => {
      logger.debug(
        'React app route change: %s (action: %s)',
        state.location.pathname,
        state.historyAction
      )
    })

    const root = createRoot(container)
    root.render(
      <AppErrorBoundary>
        <ThemeProvider theme={theme}>
          {/*
            @loadedhub widgets (ui-kit) size off --loaded-ui-unit, which defaults
            to 0.625rem (10px at a 16px root). Mercury's 10px root shrinks that to
            6.25px, so they render too small. 1rem (= 10px here) restores intended
            sizes. Keep on :root - widgets portal dialogs/toasts to document.body.
          */}
          <GlobalStyles styles={{ ':root': { '--loaded-ui-unit': '1rem' } }} />
          <SnackbarProvider {...snackbarProviderProps}>
            <QueryClientProvider client={singletonQueryClient}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={'en-nz'}
              >
                {/*
                  The topbar is rendered inside the router's root route element
                  (see components/Routes.tsx), NOT here, so that it has the
                  RouterProvider context its nav hooks (useNavigate via
                  useReactNavigate/useAngularJSNavigate) require. It still portals
                  into the separate #loaded-topbar mount node.
                */}
                <RouterProvider router={router} />
              </LocalizationProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </AppErrorBoundary>
    )
  }
})
